import type { Tracker, TrackEvent } from '../Tracker';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/** Append the official gtag.js script for `tagId`. Mock in tests to avoid network. */
export const injectGtag = (tagId: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load gtag.js for ${tagId}`));
    document.head.appendChild(script);
  });

/**
 * A {@link Tracker} backed by Google Analytics 4 (gtag.js). Pass the GA4
 * measurement ID. An empty/undefined id yields a disabled tracker (no script,
 * no events), so dev/local works with no config.
 *
 * @example
 * ```ts
 * configureTracking({ tracker: googleAnalytics(import.meta.env.VITE_GOOGLE_TAG_ID) });
 * ```
 */
/** How long `send` waits for GA to confirm an event before resolving anyway. */
export const SEND_TIMEOUT_MS = 1500;

export function googleAnalytics(tagId?: string): Tracker {
  let ready = false;

  const tracker: Tracker = {
    init() {
      if (!tagId || ready || typeof window === 'undefined') return;
      ready = true;
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', tagId, { debug_mode: true });
      if (typeof document !== 'undefined') {
        void injectGtag(tagId).catch((error) => console.error('tracking/ga:', error));
      }
    },

    track({ name, category, label, value, props }: TrackEvent) {
      if (!tagId || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
      window.gtag('event', name, {
        event_category: category,
        event_label: label,
        value,
        ...props,
      });
    },

    /**
     * The event as a beacon, resolved by gtag's own `event_callback` once it is sent. The
     * timeout is not optional: an ad blocker stops gtag.js from ever loading, the callback never
     * fires, and a page that waits for it never leaves.
     */
    send(event: TrackEvent): Promise<void> {
      tracker.init();
      if (!tagId || typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, SEND_TIMEOUT_MS);
        const done = () => {
          window.clearTimeout(timer);
          resolve();
        };
        window.gtag('event', event.name, {
          event_category: event.category,
          event_label: event.label,
          value: event.value,
          ...event.props,
          transport_type: 'beacon',
          event_callback: done,
          event_timeout: SEND_TIMEOUT_MS,
        });
      });
    },

    pageView(path: string) {
      if (!tagId || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
      window.gtag('event', 'page_view', { page_path: path });
    },
  };
  return tracker;
}
